import { Col, Row } from 'react-bootstrap'
import { useMemo, useState } from 'react'
import TableIncludedRows from './TableIncludedRows'
import TableExcludedRows from './TableExcludedRows'
import useInit from './hooks/useInit'
import handleExcludeRowParent from './utils/handleExcludeRow'
import handleIncludeRowParent from './utils/handleIncludeRow'
import Entity from './types/Entity'
import EditModal from 'components/EditModal'
import ModalEditableProps from 'components/EditModal/types/ModalEditableProps'
import useInitSearch from './hooks/useInitSearch'
import useInitPagination from './hooks/useInitPagination'
import useOnIncludedRowsChange from './hooks/useOnIncludedRowsChange'

interface AddRemoveTableSortableProps {
  getAllRowsUrl: string
  addRemoveRowUrl: string
  relationalFieldName: string
  toggleActiveUrl?: string
  sortRowsUrl?: string
  maxTableHeight: string
  entityNameSingular: string
  entityNamePlural: string
  setShowOverlay: Function
  wrapperCssClasses?: string
  editFormComponent?: React.FunctionComponent<ModalEditableProps>
  /** onAddItem used to call an action instead of a default one (add item to a state when creating a parent record). Item is not added to the selected list when the function returns `false` */
  onAddItem?: (entity: Entity) => boolean
  /** onAddAllItems used to call bulk add all action instead of a default one, if the action returns true, then the useInit is called again */
  onAddAllItems?: (entities: Entity[]) => Promise<boolean>
  /** onAddItem used to call a bulk action instead of a onAddItem on each entity */
  onAddAllItemsInvertedInitialSelection?: (entities: Entity[]) => boolean
  /** onRemoveItem used to call an action instead of a default one (add item from a state when creating a parent record). Item is not removed from the selected list when the function returns `false` */
  onRemoveItem?: (entity: Entity) => boolean
  /** onRemoveAllItems used to call bulk remove all action instead of a default one, if the action returns true, then the useInit is called again */
  onRemoveAllItems?: (entities: Entity[], setIncludedRows: React.Dispatch<React.SetStateAction<Entity[]>>) => Promise<boolean>
  /** if true, then the All items are inverted with Selected items */
  invertInitialSelection?: boolean
  /** if true, then Select All button is displayed in the All list */
  showSelectAll?: boolean
  /** if true, then Deselect All button is displayed in the Selected list */
  showDeselectAll?: boolean
  /** if true, then Search panel is enabled - display condition is evaluated by its internal state */
  enableSearch?: boolean
  /** if true, then Pagination is enabled - display condition is evaluated by its internal state */
  enablePagination?: boolean
   /** if true, then a loading spinner is displayed next to main title on data init */
  showDataInitSpinner?: boolean
  /**
   * if true, then items with isRecent === true are visible, other items are invisible
   */
  showRecentOnly?: boolean
  /** Called whenever includedRows changes. Allows parent to read current selected rows. */
  onIncludedRowsChange?: (rows: Entity[]) => void
}

const AddRemoveTableSortable: React.FunctionComponent<AddRemoveTableSortableProps> = (
  props: AddRemoveTableSortableProps,
) => {


  const [wrapperCssClasses] = useState<string>(props.wrapperCssClasses ?? '')
  
  const [includedRows, setIncludedRows] = useState<Array<Entity>>([])

  const [excludedRows, setExcludedRows] = useState<Array<Entity>>([])

  const [cssClasses] = useState<string>('overflow-auto')
  const [styles] = useState<React.CSSProperties>({ maxHeight: props.maxTableHeight })

  const [disableActions, setDisableActions] = useState<boolean>(false)

  const [showModal, setShowModal] = useState<boolean>(false)
  const [currentEditRow, setCurrentEditRow] = useState<Entity | undefined>(undefined)
  const [internalCounter, setInternalCounter] = useState<number>(0)

  const [limitShowSearchAndPagination] = useState<number>(15)
  const [showSearch, setShowSearch] = useState<boolean>(false)
  const [showPagination, setShowPagination] = useState<boolean>(false)
  const [showDataInitSpinner, setShowDataInitSpinner] = useState<boolean>(props.showDataInitSpinner ?? false)


  const filteredExcludedRows = useMemo(() => {
    if (props.showRecentOnly) {
      return excludedRows.filter((row) => row.isRecent);
    }
    return excludedRows;
  }, [excludedRows, props.showRecentOnly]);

    const filteredIncludedRows = useMemo(() => {
    if (props.showRecentOnly) {
      return includedRows.filter((row) => row.isRecent);
    }
    return includedRows;
  }, [includedRows, props.showRecentOnly]);

  const handleModalRowEdit = (row: Entity) => {
    setCurrentEditRow((prev) => {
      return { ...prev, ...row }
    })
    setShowModal(true)
  }

  const handleExcludeRow = (row: Entity) => {
    return handleExcludeRowParent({
      row: row,
      url: props.addRemoveRowUrl,
      includedRows,
      excludedRows,
      setDisableActions,
      setExcludedRows,
      setIncludedRows,
      onRemoveItem: props.onRemoveItem,
      setShowOverlay: props.setShowOverlay,
      relationalFieldName: props.relationalFieldName,
    })
  }

  const handleIncludeRow = (row: Entity) => {
    return handleIncludeRowParent({
      row: row,
      url: props.addRemoveRowUrl,
      includedRows,
      excludedRows,
      setDisableActions,
      setExcludedRows,
      setIncludedRows,
      setShowOverlay: props.setShowOverlay,
      onAddItem: props.onAddItem,
      relationalFieldName: props.relationalFieldName,
    })
  }

  const deselectAll = async () => {
    props.setShowOverlay(true)

    if(props.onRemoveAllItems !== undefined){
       const ret : boolean = await props.onRemoveAllItems(filteredIncludedRows, setIncludedRows)
       if(ret){
          setInternalCounter(prev => prev + 1)
       }
    } 
    else {
      for (const entity of filteredIncludedRows) {
        await handleExcludeRow(entity)
      }
    }
    props.setShowOverlay(false)
  }

  const selectAll = async () => {
    props.setShowOverlay(true)

    if(props.onAddAllItems !== undefined){
       const ret : boolean = await props.onAddAllItems(filteredExcludedRows)
       if(ret){
          setInternalCounter(prev => prev + 1)
       }
    } else {
      for (const entity of filteredExcludedRows) {
        await handleIncludeRow(entity)
      }
    }
    props.setShowOverlay(false)
  }

  useInit({
    getAllRowsUrl: props.getAllRowsUrl,
    setIncludedRows,
    setExcludedRows,
    internalCounter,
    includedRows,
    onAddItem: props.onAddItem,
    onAddAllItemsInvertedInitialSelection: props.onAddAllItemsInvertedInitialSelection,
    relationalFieldName: props.relationalFieldName,
    invertInitialSelection: props.invertInitialSelection,
    handleIncludeRow: handleIncludeRow,
    setShowDataInitSpinner,
  })

  useInitSearch({
    limit: limitShowSearchAndPagination,
    enableSearch: props.enableSearch ?? false,
    includedRows,
    excludedRows,
    setShowSearch,
  })

  useInitPagination({
    limit: limitShowSearchAndPagination,
    enablePagination: props.enablePagination ?? false,
    includedRows,
    excludedRows,
    setShowPagination,
  })

  useOnIncludedRowsChange({
    includedRows,
    onIncludedRowsChange: props.onIncludedRowsChange,
  })

  return (
    <div className={'container-fluid mt-4 p-0 ' + wrapperCssClasses}>
      <Row className='add-remove-tables'>
        <Col lg='12' className='mb-2'>
          <h5 className='my-3'>
            {props.entityNamePlural}
            {showDataInitSpinner && (
              <>
                &nbsp;
                <span
                    className='spinner-border spinner-border-sm text-primary'
                    role='status'
                    aria-hidden='true'
                    title='Loading data'
                    >
                </span>
              </>
            )}
          </h5>
        </Col>
        <Col lg='6'>
          <h6 className='mb-4'>Available</h6>
          <div className={cssClasses + ' excluded-rows'} style={styles}>
            {props.showSelectAll === true && (
              <a
                href='#'
                onClick={(e) => {
                  e.preventDefault()
                  if(excludedRows.length > 0){
                    selectAll()
                  }
                }}
                className={'small' + ' ' + (excludedRows.length > 0 ? '' : 'link-disabled')}
              >
                Select All
              </a>
            )}
            <TableExcludedRows
              rows={filteredExcludedRows}
              relationalFieldName={props.relationalFieldName}
              toggleActiveUrl={props.toggleActiveUrl}
              setRows={setExcludedRows}
              handleIncludeRow={handleIncludeRow}
              disableActions={disableActions}
              setDisableActions={setDisableActions}
              handleModalRowEdit={props.editFormComponent ? handleModalRowEdit : undefined}
              showSearch={showSearch}
              showPagination={showPagination}
            />
          </div>
        </Col>
        <Col lg='6'>
          <h6 className='mb-4'>Selected</h6>
          {props.showDeselectAll === true && (
            <a
              href='#'
              onClick={(e) => {
                e.preventDefault()
                if(includedRows.length > 0){
                  deselectAll()
                }
              }}
              className={'small' + ' ' + (includedRows.length > 0 ? '' : 'link-disabled')}
            >
              Deselect All
            </a>
          )}
          <div className={cssClasses + ' included-rows'} style={styles}>
            <TableIncludedRows
              rows={filteredIncludedRows}
              relationalFieldName={props.relationalFieldName}
              toggleActiveUrl={props.toggleActiveUrl}
              setRows={setIncludedRows}
              handleExcludeRow={handleExcludeRow}
              sortRowsUrl={props.sortRowsUrl}
              disableActions={disableActions}
              setDisableActions={setDisableActions}
              handleModalRowEdit={props.editFormComponent ? handleModalRowEdit : undefined}
              showSearch={showSearch}
              showPagination={showPagination}
            />
          </div>
        </Col>
      </Row>

      <EditModal
        row={currentEditRow}
        entityName={props.entityNameSingular}
        showModal={showModal}
        setShowModal={setShowModal}
        formComponent={props.editFormComponent}
        relationalFieldName={props.relationalFieldName}
        setInternalCounter={setInternalCounter}
      />
    </div>
  )
}

export default AddRemoveTableSortable
